<!-- 数据中心的经营概览、订单分析、商品分析与门店排行展示。数据请求由父页面处理。 -->
<template>
  <section class="data-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <div class="filter-bar__meta">
          <span>数据来自后端实时聚合，金额按分读取并在页面统一换算为元。</span>
        </div>
      </div>

      <div v-if="section === 'overview'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>指标</th>
              <th>今日</th>
              <th>昨日</th>
              <th>趋势</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in overview?.metrics ?? []" :key="item.metric">
              <td><span class="cell-text">{{ item.metric }}</span></td>
              <td><span class="cell-text">{{ item.today }}</span></td>
              <td><span class="cell-text">{{ item.yesterday }}</span></td>
              <td><span :class="['status-tag', toneClass(dataTrendTone(item.trend))]">{{ item.trend }}</span></td>
            </tr>
            <tr v-if="!(overview?.metrics?.length) && !loadingOverview"><td colspan="4"><el-empty description="暂无经营概览数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'orders'" class="analysis-grid">
        <section class="analysis-block">
          <h3>近 7 日支付订单</h3>
          <table>
            <thead>
              <tr>
                <th>日期</th>
                <th class="is-right">订单数</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in orderAnalysis?.dailyOrders ?? []" :key="item.label">
                <td>{{ item.label }}</td>
                <td class="is-right">{{ item.value }}</td>
              </tr>
              <tr v-if="!(orderAnalysis?.dailyOrders?.length) && !loadingOrders"><td colspan="2"><el-empty description="暂无订单趋势数据" /></td></tr>
            </tbody>
          </table>
        </section>
        <section class="analysis-block">
          <h3>订单状态分布</h3>
          <table>
            <thead>
              <tr>
                <th>订单状态</th>
                <th class="is-right">订单数</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in orderAnalysis?.statusDistribution ?? []" :key="item.label">
                <td><span :class="['status-tag', toneClass(dataOrderStatusTone(item.label))]">{{ getDataOrderStatusText(item.label) }}</span></td>
                <td class="is-right">{{ item.value }}</td>
              </tr>
              <tr v-if="!(orderAnalysis?.statusDistribution?.length) && !loadingOrders"><td colspan="2"><el-empty description="暂无状态分布数据" /></td></tr>
            </tbody>
          </table>
        </section>
      </div>

      <div v-else-if="section === 'products'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>商品</th>
              <th>商品 ID</th>
              <th class="is-right">销量</th>
              <th class="is-right">销售额</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in productAnalysis?.items ?? []" :key="item.productId">
              <td><span class="cell-text">{{ item.productName || item.productId }}</span></td>
              <td><span class="cell-text">{{ item.productId }}</span></td>
              <td class="is-right">{{ item.sales }}</td>
              <td class="is-right">{{ formatAmount(item.salesAmount) }}</td>
            </tr>
            <tr v-if="!(productAnalysis?.items?.length) && !loadingProducts"><td colspan="4"><el-empty description="暂无商品分析数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>排名</th>
              <th>门店</th>
              <th class="is-right">营业额</th>
              <th class="is-right">订单数</th>
              <th class="is-right">客单价</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in storeRanks" :key="item.storeId">
              <td>{{ index + 1 }}</td>
              <td><span class="cell-text">{{ item.storeName || item.storeId }}</span></td>
              <td class="is-right">{{ formatAmount(item.revenue) }}</td>
              <td class="is-right">{{ item.orderCount }}</td>
              <td class="is-right">{{ formatAmount(item.avgPrice) }}</td>
            </tr>
            <tr v-if="!storeRanks.length && !loadingStoreRank"><td colspan="5"><el-empty description="暂无门店排行数据" /></td></tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { toRefs } from 'vue'
import type { DataOrderAnalysis, DataOverview, DataProductAnalysis, StoreRankItem } from '@/api/data'
import type { OperationStat } from '@/data/operationDesign'

type DataSection = 'overview' | 'orders' | 'products' | 'stores'
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  section: DataSection
  stats: OperationStat[]
  overview?: DataOverview
  orderAnalysis?: DataOrderAnalysis
  productAnalysis?: DataProductAnalysis
  storeRanks: StoreRankItem[]
  loadingOverview: boolean
  loadingOrders: boolean
  loadingProducts: boolean
  loadingStoreRank: boolean
  formatAmount: (amountInFen: number) => string
  getDataOrderStatusText: (status?: string) => string
  dataTrendTone: (trend?: string) => StatusTone
  dataOrderStatusTone: (status?: string) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const {
  section,
  stats,
  overview,
  orderAnalysis,
  productAnalysis,
  storeRanks,
  loadingOverview,
  loadingOrders,
  loadingProducts,
  loadingStoreRank,
  formatAmount,
  getDataOrderStatusText,
  dataTrendTone,
  dataOrderStatusTone,
  toneClass,
} = toRefs(props)
</script>

<style scoped lang="scss">
.data-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-bar__meta { flex: 1; color: var(--jf-color-text-secondary); font-size: 13px; }
.filter-bar__actions { display: flex; gap: 8px; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button { height: 36px; padding: 0 16px; border-radius: 8px; background: var(--jf-color-primary); color: #fff; font-size: 14px; font-weight: 700; }
.table-wrap { width: 100%; overflow-x: auto; }
.analysis-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; padding: 16px; background: var(--jf-color-background); }
.analysis-block { border: 1px solid var(--jf-color-border); border-radius: 10px; background: #fff; overflow: hidden; }
.analysis-block h3 { margin: 0; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); font-size: 15px; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
@media (max-width: 1180px) {
  .summary-grid, .analysis-grid { grid-template-columns: 1fr; }
}
</style>
